import { Send } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { MAX_CONVERSATION_MESSAGE_LENGTH } from "@/api/agent-conversation";
import { FOCUS_RING_CLASS } from "@/lib/a11y";
import { cn } from "@/lib/utils";

/**
 * O único campo de texto do terminal. Abre só quando o agente espera a pessoa
 * (ver `canType`); fechado, mostra a razão em vez de deixar a pessoa digitar no
 * escuro. Enter envia; Shift+Enter quebra a linha.
 */
export function ConversationComposer({
  disabled,
  hint,
  replying,
  maxLength = MAX_CONVERSATION_MESSAGE_LENGTH,
  onSend,
}: {
  disabled: boolean;
  /** Por que o campo está fechado (agente trabalhando, conversa encerrada). */
  hint?: string | null;
  /** Há uma pergunta do agente esperando: o campo a responde. */
  replying: boolean;
  /** Quanto a rota de quem recebe aceita (o Nó 02 aceita menos que a conversa). */
  maxLength?: number;
  onSend: (text: string) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState("");

  const submit = () => {
    const value = text.trim();
    if (!value || disabled || text.length > maxLength) return;
    onSend(value);
    setText("");
  };

  return (
    <form
      className="flex flex-col gap-2.5 border-t border-[rgba(255,255,255,0.08)] px-4 pb-3 pt-3.5"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      {hint && (
        <p data-testid="conversation-composer-hint" className="m-0 text-xs text-[rgba(255,255,255,0.55)]">
          {hint}
        </p>
      )}
      <div
        className={cn(
          "flex items-end gap-2 rounded-lg border bg-[rgba(255,255,255,0.04)] py-2 pl-3 pr-2 focus-within:border-[rgba(255,200,61,0.5)]",
          replying && !disabled ? "border-[#ffc83d]" : "border-[rgba(255,255,255,0.12)]",
          disabled && "opacity-60",
        )}
      >
        <textarea
          aria-label={t("Message the agent")}
          placeholder={replying ? t("Answer the agent") : t("Message the agent")}
          value={text}
          disabled={disabled}
          rows={2}
          maxLength={maxLength}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              submit();
            }
          }}
          className="max-h-[120px] min-h-[42px] flex-1 resize-none border-none bg-transparent font-sans text-sm leading-normal text-white outline-none placeholder:text-[rgba(255,255,255,0.35)] disabled:cursor-not-allowed"
        />
        <button
          type="submit"
          disabled={disabled || !text.trim() || text.length > maxLength}
          className={cn(
            "inline-flex h-9 shrink-0 items-center gap-2 rounded-md bg-[#ffc83d] px-3.5 font-sans text-sm font-semibold text-[#08254c] transition-colors hover:bg-[#ffd466]",
            "disabled:cursor-not-allowed disabled:bg-[rgba(255,200,61,0.35)] disabled:text-[rgba(8,37,76,0.7)]",
            FOCUS_RING_CLASS,
          )}
        >
          <Send aria-hidden="true" className="h-3.5 w-3.5" />
          {t("Send")}
        </button>
      </div>
      <p className="m-0 self-end font-mono text-[11px] text-[rgba(255,255,255,0.4)]">
        {text.length} / {maxLength}
      </p>
    </form>
  );
}
